import { useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import CartOffCanvas from "../../components/CartOffCanvas";
import { showError } from "../../utils/toastUtils";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";

const HeartIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const CartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <path d="M16 10a4 4 0 0 1-8 0" />
  </svg>
);

export default function ProductCard({ product }) {
  const videoRef = useRef(null);
  const [addedToCart, setAddedToCart] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(product.isWishlisted || false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { addToCart } = useCart();
  const navigate = useNavigate();

  const handleMouseEnter = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, []);

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: product._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await addToCart(product._id, 1, null);
      setAddedToCart(true);
      setIsCartOpen(true);
      setTimeout(() => setAddedToCart(false), 2000);
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  const displayImage =
    product.image ||
    (product.gallery && product.gallery[0]) ||
    "/placeholder.jpg";

  return (
    <>
      <article
        className="pcard"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => navigate(`/shop/${product.slug || product._id}`)}
      >
        <div className="pcard-media">
          {product.badge && <span className="pcard-badge">{product.badge}</span>}

          <img
            className={`pcard-image${product.video ? " has-video" : ""}`}
            src={displayImage}
            alt={product.name}
            loading="lazy"
          />

          {product.video && (
            <video ref={videoRef} className="pcard-video" muted loop playsInline preload="none" aria-hidden="true">
              <source src={product.video} type="video/mp4" />
            </video>
          )}

          <div className="pcard-media-overlay" aria-hidden="true" />

          <div className="pcard-actions">
            {/* Wishlist */}
            <button
              className={`pcard-action-btn${isWishlisted ? " wishlisted" : ""}`}
              onClick={handleWishlist}
              aria-label={isWishlisted ? "Remove from wishlist" : "Add to wishlist"}
            >
              <HeartIcon />
            </button>

            {/* Add to Cart */}
            <button
              className= {` pcard-add-to-cart pcard-action-btn${addedToCart ? " wishlisted" : ""}`}
              onClick={handleAddToCart}
              aria-label="Add to cart"
            >
              {addedToCart ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              ) : (
                <span> <CartIcon />Add to cart</span>
               
              )}
            </button>
          </div>
        </div>

        <div className="pcard-content px-3">
          {product.craft && <span className="pcard-craft">{product.craft}</span>}

          <Link
            to={`/shop/${product.slug || product._id}`}
            className="pcard-name"
            onClick={(e) => e.stopPropagation()}
          >
            {product.name}
          </Link>

          <div className="pcard-price-row">
            <span className="pcard-price">₹{product.price.toLocaleString("en-IN")}</span>
            {product.originalPrice && (
              <span className="pcard-price-original">₹{product.originalPrice.toLocaleString("en-IN")}</span>
            )}
          </div>
        </div>

        <div className="pcard-rule" aria-hidden="true" />
      </article>

      <CartOffCanvas isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
}